import "bootstrap/dist/css/bootstrap.min.css";
import * as d3 from "d3";
import React, { useEffect, useRef, useCallback } from "react";
import { useWebSocket } from "utils/hooks";

const RealtimeAreaGraph = ({
  width,
  height,
  interferences,
  initialDataset,
  defaultOptiopns,
  lookback,
  freeze,
}) => {
  const ref = useRef(null);
  const dataRef = useRef([]);
  const graphVarsRef = useRef([]);

  const scale = 0.5;
  const lookback_s = lookback ? lookback : 30;
  const room_for_axis = 50;
  const room_for_title = defaultOptiopns?.title ? 20 : 0;
  const room_for_legend = defaultOptiopns?.legendValues?.length > 0 ? 25 : 0;
  const updateIntervalMs = 200;

  const updateTimer = useRef();

  const interferenceValuesRef = useRef([]);

  useEffect(() => {
    const yPoints = interferences
      ?.flat()
      .filter((event) => Object.keys(event).length > 0)
      .map((i) => i?.interferenceFrequency && i?.interferenceFrequency);

    if (yPoints?.length > 0) interferenceValuesRef.current = yPoints;
    else interferenceValuesRef.current = [];

    // Add interference events
    interferenceValuesRef.current.forEach((val) => {
      val && dataRef.current.push([new Date(), val]);
    });
    interferenceValuesRef.current = [];
  }, [interferences]);

  /** Initialize graph once */
  useEffect(() => {
    if (!ref.current) return;

    const svg = d3
      .select(ref.current)
      .attr("width", width)
      .attr("height", height + room_for_title);

    const thresholds = svg.append("g").attr("id", "thresholds");
    const areaGroup = svg.append("g").attr("id", "area");
    const lineGroup = svg.append("g").attr("id", "line");
    const yScale = d3
      .scaleLinear()
      .domain([defaultOptiopns.yMinValue, defaultOptiopns.yMaxValue])
      .range([height - room_for_axis - room_for_legend, 10]);
    const xScaleGroup = svg.append("g");
    const yScaleGroup = svg.append("g");
    const xGridsGroup = svg
      .append("g")
      .attr("id", "xGridsGroup")
      .attr("opacity", "0.3");
    const yGridsGroup = svg
      .append("g")
      .attr("id", "yGridsGroup")
      .attr("opacity", "0.3");
    const legend = svg.append("g").attr("id", "legends");

    const title = svg
      .append("text")
      .attr("transform", "translate(" + width / 2 + ",0)")
      .attr("y", 15)
      .attr("font-size", "15px")
      .attr("text-anchor", "middle")
      .attr("stroke", "white")
      .attr("letter-spacing", "0.054em")
      .text(defaultOptiopns.title);

    if (defaultOptiopns.xLabelName) {
      const xAxisLabel = svg
        .append("g")
        .attr("id", "xAxisLabel")
        .attr("transform", "translate(" + 35 + "," + 35 + ")");

      xAxisLabel
        .append("text")
        .attr("y", height - room_for_axis + room_for_title)
        .attr("x", width / 2 - room_for_axis / 2)
        .style("fill", "white")
        .attr("font-size", "12px")
        .attr("text-anchor", "middle")
        .text(defaultOptiopns.xLabelName);
    }

    if (defaultOptiopns.yLabelName) {
      const yAxisLabel = svg
        .append("g")
        .attr("id", "yAxisLabel")
        .attr("transform", "translate(" + 35 + "," + 35 + ")");

      yAxisLabel
        .append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -height / 2 + room_for_axis - room_for_title)
        .attr("y", 30)
        .attr("dy", "-4.7em")
        .style("fill", "white")
        .attr("font-size", "12px")
        .attr("text-anchor", "middle")
        .text(defaultOptiopns.yLabelName);
    }

    // Draw legend (static)
    defaultOptiopns?.thresholds?.forEach((element, index) => {
      const xPos =
        room_for_axis + (width / defaultOptiopns.thresholds.length) * index;
      legend
        .append("rect")
        .attr("x", xPos)
        .attr("y", height - room_for_legend)
        .attr("width", 16)
        .attr("height", 16)
        .attr("stroke", "black")
        .attr("fill", element.color);

      legend
        .append("text")
        .attr("x", xPos + 20)
        .attr("y", height - room_for_legend + 8)
        .attr("font-size", 14)
        .attr("alignment-baseline", "middle")
        .attr("fill", "white")
        .text(element.label);
    });

    // Draw thresholds
    defaultOptiopns?.thresholds?.forEach((element) => {
      thresholds
        .append("line")
        .style("stroke", element.color)
        .style("stroke-width", 2)
        .attr("x1", room_for_axis)
        .attr("y1", yScale(element.value))
        .attr("x2", width)
        .attr("y2", yScale(element.value));
    });

    graphVarsRef.current = [
      svg,
      areaGroup,
      lineGroup,
      xScaleGroup,
      xGridsGroup,
      yScale,
      yScaleGroup,
      yGridsGroup,
      thresholds,
      legend,
    ];

    // Seed initial data
    const now = new Date();
    //dataRef.current.push([now, 0]);

    initialDataset?.forEach((sample) => {
      sample.interferenceFrequency &&
        dataRef.current.push([sample.timestamp, sample.interferenceFrequency]);
    });

    /** Interval updater */
    updateTimer.current = setInterval(() => {
      const now = new Date();
      const lookback = new Date(now.getTime() - lookback_s * 1000);

      // Clean old points
      dataRef.current = dataRef.current.filter((d) => d[0] > lookback);

      render(dataRef.current, now, lookback, graphVarsRef.current);
    }, updateIntervalMs);

    return () => clearInterval(updateTimer.current);
  }, []);

  useEffect(() => {
    freeze === true && clearInterval(updateTimer.current);
    const now = new Date();
    const lookback = new Date(now.getTime() - lookback_s * 1000);

    // Add interference events
    interferenceValuesRef.current.forEach((val) => {
      val && dataRef.current.push([now, val]);
    });
    interferenceValuesRef.current = [];

    // Clean old points
    dataRef.current = dataRef.current.filter((d) => d[0] > lookback);

    render(dataRef.current, now, lookback, graphVarsRef.current);
  }, [freeze]);

  /** Render dynamic updates */
  const render = (data, now, lookback, graphVars) => {
    const [
      svg,
      areaGroup,
      lineGroup,
      xScaleGroup,
      xGridsGroup,
      yScale,
      yScaleGroup,
      yGridsGroup,
    ] = graphVars;

    // data.sort((a, b) => a[0] - b[0]);

    const xScale = d3
      .scaleUtc()
      .domain([lookback, now])
      .range([room_for_axis + 5, width]);

    if (!defaultOptiopns.yMaxValue)
      yScale.domain([
        defaultOptiopns.yMinValue,
        data.reduce((max, d) => {
          const value = d[1];
          return value > max ? value : max;
        }, 0),
      ]);

    const areaGenerator = d3
      .area()
      .x((d) => xScale(d[0]))
      .y0(height - room_for_axis - room_for_legend) // baseline
      .y1((d) => yScale(d[1]))
      .curve(d3.curveStep); // step curve

    // JOIN
    const areaPath = areaGroup
      .selectAll("path")
      .attr("transform", `translate(-5,${room_for_title})`)
      .data([data]);

    // EXIT
    areaPath.exit().remove();

    // UPDATE
    areaPath.attr("d", areaGenerator).attr("fill", "red").attr("opacity", 0.4);

    // ENTER
    areaPath
      .enter()
      .append("path")
      .attr("d", areaGenerator)
      .attr("fill", "red")
      .attr("opacity", 0.4);

    const lineGenerator = d3
      .line()
      .x((d) => xScale(d[0]))
      .y((d) => yScale(d[1]))
      .curve(d3.curveStep); // step curve

    // JOIN
    const linePath = lineGroup
      .selectAll("path")
      .attr("transform", `translate(-5,${room_for_title})`)
      .data([data]);

    // EXIT
    linePath.exit().remove();

    // UPDATE
    linePath
      .attr("d", lineGenerator)
      .attr("fill", "none")
      .attr("stroke", "red")
      .attr("stroke-width", 2)
      .attr("opacity", 1);

    // ENTER
    linePath
      .enter()
      .append("path")
      .attr("d", lineGenerator)
      .attr("fill", "red")
      .attr("opacity", 0.4);

    // Update axes
    const xAxis = d3.axisBottom(xScale).tickFormat(d3.utcFormat("%H:%M:%S"));
    xScaleGroup
      .attr(
        "transform",
        `translate(-5,${height - room_for_axis - room_for_legend + room_for_title})`,
      )
      .call(xAxis);

    const yAxis = d3.axisLeft(yScale);

    if (defaultOptiopns.yTicks && defaultOptiopns.yMaxValue) {
      const step =
        (defaultOptiopns.yMaxValue - defaultOptiopns.yMinValue) /
        defaultOptiopns.yTicks;
      yAxis.tickValues(
        d3.range(
          defaultOptiopns.yMinValue,
          defaultOptiopns.yMaxValue + step,
          step,
        ),
      );
    }

    yScaleGroup
      .attr("transform", `translate(${room_for_axis},${room_for_title})`)
      .call(yAxis);

    // Grids
    xGridsGroup
      .attr(
        "transform",
        `translate(-5,${height - room_for_axis - room_for_legend + room_for_title})`,
      )
      .call(
        d3
          .axisBottom(xScale)
          .tickSize(-height + room_for_axis + 10)
          .tickFormat(""),
      );

    yGridsGroup
      .attr("transform", `translate(${room_for_axis},${room_for_title})`)
      .call(
        d3
          .axisLeft(yScale)
          .tickSize(-width + room_for_axis + room_for_legend)
          .tickFormat(""),
      );
  };

  return (
    <div
      className="bar-graph p-2"
      id="container"
      style={{ justifyContent: "center" }}
    >
      <svg
        ref={ref}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: "100%",
          // height: "auto",
          display: "block",
        }}
      ></svg>
    </div>
  );
};

export default RealtimeAreaGraph;
